type FeatureConfig = Record<string, unknown> | undefined;
type HostLocationMap = Record<string, string> | undefined;
type HostTitleMap = Record<string, string> | undefined;

const DEFAULT_LOCATION = 'penicuik';

const normalizeLocationKey = (value: string) => value.replace(/^\/+/, '').toLowerCase();

const getPathSegment = (pathname: string) =>
  normalizeLocationKey(pathname.split('/').filter(Boolean)[0] || '');

const normalizeHostname = (hostname: string) => hostname.toLowerCase().split(':')[0];

const getMappedLocation = (hostname: string, locationByHost: HostLocationMap) => {
  if (!locationByHost) {
    return '';
  }

  const normalizedHost = normalizeHostname(hostname);
  const directMatch = locationByHost[normalizedHost];

  if (directMatch) {
    return normalizeLocationKey(directMatch);
  }

  const withoutWww = normalizedHost.replace(/^www\./, '');
  return locationByHost[withoutWww] ? normalizeLocationKey(locationByHost[withoutWww]) : '';
};

const getMappedTitle = (hostname: string, titleByHost: HostTitleMap, defaultTitle: string) => {
  if (!titleByHost) {
    return defaultTitle;
  }

  const normalizedHost = normalizeHostname(hostname);
  const directMatch = titleByHost[normalizedHost];

  if (directMatch) {
    return directMatch;
  }

  const withoutWww = normalizedHost.replace(/^www\./, '');
  return titleByHost[withoutWww] || defaultTitle;
};

const resolveLocationFromUrl = ({
  pathname,
  hostname,
  featuresByLocation,
  locationByHost,
  defaultLocation = DEFAULT_LOCATION,
}: {
  pathname: string;
  hostname: string;
  featuresByLocation?: FeatureConfig;
  locationByHost?: HostLocationMap;
  defaultLocation?: string;
}) => {
  const knownLocations = new Set(
    Object.keys(featuresByLocation || {}).map((key) => normalizeLocationKey(key)),
  );
  const mappedLocation = getMappedLocation(hostname, locationByHost);
  const fallbackLocation = normalizeLocationKey(defaultLocation);
  const pathSegment = getPathSegment(pathname);

  if (pathSegment) {
    const isKnownPathLocation = knownLocations.has(pathSegment);
    if (isKnownPathLocation) {
      return {
        locationId: pathSegment,
        isPathScoped: true,
        isKnownLocation: true,
      };
    }

    const resolvedLocation = mappedLocation || fallbackLocation;
    return {
      locationId: resolvedLocation,
      isPathScoped: false,
      isKnownLocation: knownLocations.has(resolvedLocation),
    };
  }

  const resolvedLocation = mappedLocation || fallbackLocation;

  return {
    locationId: resolvedLocation,
    isPathScoped: false,
    isKnownLocation: knownLocations.has(resolvedLocation),
  };
};

const resolveTitleFromHost = ({
  hostname,
  titleByHost,
  defaultTitle = 'Cuikbook',
}: {
  hostname: string;
  titleByHost?: HostTitleMap;
  defaultTitle?: string;
}) => getMappedTitle(hostname, titleByHost, defaultTitle);

export { DEFAULT_LOCATION, resolveLocationFromUrl, resolveTitleFromHost };
